import { useAuthStore } from '@/stores/auth';
import { store, key } from '@/storeVuex';
import { addSentryBreadcrumb, captureApiError } from '@/plugins/monitoring';
import { observeFromResponse } from '@/utils/permissionsVersion';
import { redirectToLogin } from '@/utils/sessionRedirect';
import { withHttpStatus } from '@/utils/errorEnvelope';

/** Opt-in transport isolation for public worker verification. Defaults preserve existing callers. */
export interface FetchRequestOptions {
    public?: boolean;
    sensitive?: boolean;
    headers?: Record<string, string>;
    statusErrors?: boolean;
    responseType?: 'blob';
}

export const fetchWrapper = {
    get: request('GET'),
    post: request('POST'),
    put: request('PUT'),
    patch: request('PATCH'),
    delete: request('DELETE')
};

function request(method: string) {
    return (url: any, body?: any, options: FetchRequestOptions = {}) => {
        // Use the URL directly without proxy conversion
        let requestUrl = url;
        // Commented out proxy conversion - use staging API directly
        // if (import.meta.env.DEV && url.includes('stagingapi.builtsimple.com.au/api')) {
        //     // Replace absolute URL with relative URL to use Vite proxy
        //     requestUrl = url.replace('https://stagingapi.builtsimple.com.au', '');
        // }

        const requestOptions: any = {
            method,
            // A caller's headers go first, so the auth header this wrapper owns can't be replaced by one.
            headers: { ...options.headers, ...(options.public ? { 'X-Requested-With': 'XMLHttpRequest' } : authHeader(url)) }
        };
        // GET/HEAD requests cannot legally carry a body — fetch throws
        // "Request with GET/HEAD method cannot have body". If a caller passes one
        // by mistake, ignore it (GET data belongs in the query string) rather than
        // crashing. Attach the body only for methods that allow one.
        if (body && method !== 'GET' && method !== 'HEAD') {
            // Check if body is FormData (for file uploads)
            if (body instanceof FormData) {
                // Don't set Content-Type for FormData - browser will set it with boundary
                requestOptions.body = body;
            } else {
                requestOptions.headers['Content-Type'] = 'application/json';
                requestOptions.body = JSON.stringify(body);
            }
        }
        if (!options.sensitive) addSentryBreadcrumb('api', `${method} ${requestUrl}`, {
            url: requestUrl,
            method,
            hasBody: !!body,
        });
        return fetch(requestUrl, requestOptions).then((response) => handleResponse(response, method, requestUrl, body, options));
    };
}

// helper functions

function authHeader(url: any) {
    const user: any = store.state.user;
    // return auth header with jwt if user is logged in and request is to the api url
    // const { user } = useAuthStore();
    const isLoggedIn = !!user?.token;
    // Check if it's an API URL (either absolute or relative path starting with /api)
    const gatewayUrl = (import.meta as any).env?.VITE_API_GATEWAY_URL || '';
    const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL)
        || url.startsWith('/api')
        || url.includes('builtsimple.com.au')
        // Built Simple gateway — same Bearer token works there because the
        // gateway validates it against bsapi via /api/userinfo.
        || (!!gatewayUrl && url.startsWith(gatewayUrl))
        || url.includes('localhost:3100')
        || url.includes('127.0.0.1:3100');

    const headers: any = {
        'X-Requested-With': 'XMLHttpRequest' // Required by the API
    };

    if (isLoggedIn && isApiUrl) {
        headers['Authorization'] = `Bearer ${user.token}`;
    }

    return headers;
}

function handleResponse(response: any, method?: string, url?: string, requestBody?: any, options: FetchRequestOptions = {}) {
    // Every API response carries the version of the permission map it was
    // served under; a change means this user's role was edited, so the cached
    // map is re-fetched in the background. See utils/permissionsVersion.
    // Public (unauthenticated) calls carry no user, so they have no version.
    if (!options.public) observeFromResponse(response);

    if (response.ok && options.responseType === 'blob') return response.blob();
    return response.text().then((text: any) => {
        let data: any;
        try { data = text && JSON.parse(text); }
        catch (error) {
            // Preserve existing parsing failures; opt-in callers still need an HTTP error's status.
            if (response.ok || !options.statusErrors) throw error;
            data = { message: 'The server returned an unreadable response. Please try again.' };
        }

        if (!response.ok) {
            // Report API error to Sentry with full context
            if (!options.sensitive) captureApiError(
                url || response.url || 'unknown',
                method || 'UNKNOWN',
                response.status,
                data,
                requestBody instanceof FormData ? '(FormData)' : requestBody
            );

            // Only a genuine authentication failure should end the session.
            // 401 = not authenticated (token missing/invalid/expired) -> log out.
            // 403 = authenticated but NOT permitted (a Gate/role/permission denial,
            // e.g. a non-admin hitting an admin-only route like /companysettings).
            // Treating 403 as "session expired" wrongly wiped the user's session and
            // bounced them to /login. Keep 403 out of the logout path; only fall back
            // to logout if the body explicitly says "Unauthenticated".
            const isUnauthenticated =
                response.status === 401 ||
                (data && (
                    data.message?.includes('Unauthenticated') ||
                    data.error?.includes('Unauthenticated') ||
                    JSON.stringify(data).includes('Unauthenticated')
                ));

            // Only the bsapi (Laravel) session can be "expired" — 401s from the
            // gateway, AI services, or any other origin must not nuke the user's
            // localStorage/sanctum session. Each non-bsapi caller has its own
            // error UI; we let those bubble up untouched.
            const checkUrl: string = url || response.url || '';
            const gatewayUrl: string = (import.meta as any).env?.VITE_API_GATEWAY_URL || '';
            const isGatewayCall =
                (!!gatewayUrl && checkUrl.includes(gatewayUrl)) ||
                checkUrl.includes('localhost:3100') ||
                checkUrl.includes('127.0.0.1:3100') ||
                checkUrl.includes('gateway.builtsimple.com.au');

            if (isUnauthenticated && !isGatewayCall && !options.public) {
                addSentryBreadcrumb('auth', 'Session expired - redirecting to login', {
                    status: response.status,
                    url: url || response.url,
                }, 'warning');

                // Clears storage and navigates AT MOST ONCE per document —
                // a page with a dozen calls in flight produces a dozen 401s.
                redirectToLogin();
            }

            // const error = (data && data.message) || response.statusText;
            // With the status on it, or $filters.responseMessage shows a
            // status-less Laravel error on a green success toast. Opt-in
            // (statusErrors) callers always get the real HTTP status.
            return Promise.reject(options.statusErrors ? { ...data, status: response.status } : withHttpStatus(data, response.status));
        }

        return data;
    });
}
